import { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, Zap, Database, Check, Loader2, AlertCircle, Shield, Activity } from 'lucide-react';

interface BillingData {
  success: boolean;
  subscription: {
    current_plan: string;
    price_usd: number;
    status: string;
    billing_cycle: string;
  };
  usage: {
    month: string;
    tokens: {
      used: number;
      limit: number;
      percentage: number;
    };
    api_calls: number;
    storage_mb: number;
  };
}

export default function Billing() {
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/user-panel/billing`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setData(res.data);
      } catch (err) {
        console.error(err);
        setError('Fatura ve kullanım bilgileri alınamadı.');
      } finally {
        setLoading(false);
      }
    };
    void fetchBilling();
  }, []);

  if (loading) { return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>; }
  if (error || !data) { return <div className="p-6 text-red-400 flex items-center gap-2"><AlertCircle /> {error}</div>; }

  const { subscription, usage } = data;

  const plans = [
    { name: 'free', label: 'Freemium', price: 0, features: ['1 Aktif Proje', '100K Token / Ay', 'Temel Agent Desteği', '1GB Storage'] },
    { name: 'pro', label: 'Pro', price: 25, features: ['Sınırsız Proje', '5M Token / Ay', 'Özel Cloud Agentları', '100GB Storage', 'Öncelikli İşlem'] },
    { name: 'pro_plus', label: 'Pro Plus', price: 48, features: ['Pro Özellikleri', 'Sınırsız Token', 'Otonom Dağıtım (Deploy)', 'Özel Test Agentları'] }
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-3 text-white">
          <CreditCard className="text-indigo-500" size={32} /> Fatura ve Planlar
        </h1>
        <div className="bg-gray-800 border border-gray-700 px-4 py-2 rounded-xl text-sm text-gray-300">
          Mevcut Plan: <span className="font-bold text-indigo-400 uppercase tracking-wider">{subscription.current_plan}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Kullanım İstatistikleri */}
        <div className="md:col-span-1 space-y-6">
          <div className="bg-gray-900 border border-gray-800 p-6 rounded-2xl shadow-xl">
            <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
              <ActivityIcon /> Aylık Kullanım ({usage.month})
            </h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400 flex items-center gap-1"><Zap size={14}/> LLM Token</span>
                  <span className="text-white font-mono">{usage.tokens.used.toLocaleString()} / {usage.tokens.limit.toLocaleString()}</span>
                </div>
                <div className="h-2.5 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${usage.tokens.percentage > 85 ? 'bg-red-500' : 'bg-indigo-500'}`} 
                    style={{ width: `${usage.tokens.percentage}%` }}
                  />
                </div>
                <p className="text-[10px] text-right text-gray-500 mt-1">% {usage.tokens.percentage} kullanıldı</p>
              </div>

              <div className="pt-4 border-t border-gray-800">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 flex items-center gap-1"><Database size={14}/> Depolama</span>
                  <span className="text-white font-mono">{usage.storage_mb} MB</span>
                </div>
              </div>
              
              <div className="pt-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 flex items-center gap-1"><Shield size={14}/> API Çağrıları</span>
                  <span className="text-white font-mono">{usage.api_calls}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Paket Yükseltme */}
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isActive = subscription.current_plan === plan.name;
            return (
              <div key={plan.name} className={`relative bg-gray-900 border rounded-2xl p-6 flex flex-col transition-all ${
                isActive ? 'border-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.2)]' : 'border-gray-800 hover:border-gray-600'
              }`}>
                {isActive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Mevcut Plan
                  </div>
                )}
                <div className="mb-4 text-center">
                  <h4 className="text-xl font-bold text-white capitalize">{plan.label}</h4>
                  <div className="mt-2 text-3xl font-extrabold text-white">
                    ${plan.price}<span className="text-sm font-medium text-gray-500">/ay</span>
                  </div>
                </div>
                <ul className="flex-1 space-y-3 mb-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button disabled={isActive} className={`w-full py-2.5 rounded-xl font-bold transition-all ${
                  isActive 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                }`}>
                  {isActive ? 'Aktif' : 'Yükselt'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const ActivityIcon = () => <Activity className="text-indigo-400" size={18} />;